import {
  type DependencyValidationError,
  isParameterValueAmendOperation,
} from '@festina/configuration-general-types/configuration'
import type { Optional } from '@festina-life/core'
import { type ReactNode, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router'

import {
  filterParameterDefinitionErrors,
  filterParameterValueErrors,
} from '../components/errorsPanel/helpers'
import type { ParameterRouteParams } from '../system/types'
import type { ValidationErrorWithLocation } from '../uiModels/errors/types'
import { useConfigurationOperationErrorsMap } from '../uiModels/errors/useConfigurationOperationErrorsMap'
import {
  getAmendOperationsForViewConstraints,
  limitValues,
  processValueOperationsForViewConstraints,
} from '../uiModels/parameters/helpers'
import { useParameterDefinitionOperation } from '../uiModels/parameters/useParameterDefinitionOperation'
import { useResolvedLargeParameterValues } from '../uiModels/parameters/useResolvedLargeParameterValues'
import { useApplicationViewOperationsContext } from '../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../workspace/operationsContext'
import { useConfigurationContext } from './configurationContext'
import { useLayoutContext } from './layoutContext'
import { Provider } from './parameterContext'
import { getParameterDetailsFieldFromPath } from './pathHelpers'

const MAX_VALUES_FETCHED = 100

type ParameterProviderProps = {
  children: ReactNode
}

const ParameterProvider = ({ children }: ParameterProviderProps) => {
  // region: Routing dependent params
  const { parameterDefinitionId } = useParams<ParameterRouteParams>()
  const { hash: currentHash } = useLocation()
  // endregion: Routing dependent params

  const [reviewingOperationKey, setReviewingOperationKey] =
    useState<Optional<string>>(undefined)

  const { viewAt } = useConfigurationContext()
  const parameterDefinitionOperation = useParameterDefinitionOperation({
    name: parameterDefinitionId,
    atDate: viewAt,
  })
  const { errors } = useOperationsContext()
  const { applicationParameterValues } = useApplicationViewOperationsContext()

  const parameterValueOperationsForDefnitionId = useMemo(
    () => applicationParameterValues.get(parameterDefinitionId ?? '') ?? [],
    [applicationParameterValues, parameterDefinitionId],
  )

  const resolvedParameterValueOperations = useResolvedLargeParameterValues(
    parameterValueOperationsForDefnitionId,
  )

  const reviewingOperation = useMemo(
    () =>
      resolvedParameterValueOperations?.find(
        (operation) =>
          operation.internalOperation.operationKey === reviewingOperationKey,
      ),
    [resolvedParameterValueOperations, reviewingOperationKey],
  )

  const {
    preferences: {
      parameters: { isFlattened, isTimeSeriesMode },
    },
  } = useLayoutContext()

  // region: Parameters visual model hooks
  const operationsForView = useMemo(
    () =>
      reviewingOperation
        ? [reviewingOperation]
        : resolvedParameterValueOperations,
    [reviewingOperation, resolvedParameterValueOperations],
  )

  const isReviewingValueAmend =
    reviewingOperation &&
    isParameterValueAmendOperation(reviewingOperation.internalOperation)

  const amendOperationsInView = useMemo(
    () =>
      isReviewingValueAmend
        ? [reviewingOperation]
        : getAmendOperationsForViewConstraints({
            allOperations: operationsForView,
            viewAt: isTimeSeriesMode ? viewAt : undefined,
          }),
    [
      operationsForView,
      isTimeSeriesMode,
      viewAt,
      isReviewingValueAmend,
      reviewingOperation,
    ],
  )

  const { operations: parameterValueEntityOperationsForView, hasMore } =
    useMemo(() => {
      if (amendOperationsInView.length) {
        return { operations: [], hasMore: false }
      }

      const allValuesForView = processValueOperationsForViewConstraints({
        isFlattened,
        viewAt: isTimeSeriesMode ? viewAt : undefined,
        allOperations: operationsForView,
      })

      return limitValues(
        allValuesForView,
        reviewingOperation ? undefined : MAX_VALUES_FETCHED,
      )
    }, [
      amendOperationsInView,
      isFlattened,
      isTimeSeriesMode,
      operationsForView,
      reviewingOperation,
      viewAt,
    ])

  const parameterDefinitionErrors = errors.filter(
    filterParameterDefinitionErrors,
  )
  const parameterValueErrors = errors.filter(filterParameterValueErrors)
  const definitionErrorsForCurrentParameterDefinition =
    useConfigurationOperationErrorsMap({
      errors: parameterDefinitionErrors,
      operations: parameterDefinitionOperation
        ? [parameterDefinitionOperation]
        : [],
      idFromOperation: (operation) =>
        operation.internalOperation?.entity?.name ?? '',
      idFromError: (error) => error.operationName ?? '',
    })

  const parameterValueErrorsForCurrentParameterDefinition =
    useConfigurationOperationErrorsMap({
      errors: parameterValueErrors,
      operations: resolvedParameterValueOperations,
      idFromOperation: (operation) =>
        operation.internalOperation?.entity?.name ?? '',
      idFromError: (error) => error.operationName ?? '',
    })

  const errorsForValueOperations = useMemo(() => {
    const valueOperationKeys = resolvedParameterValueOperations.map(
      (editable) => editable.internalOperation.operationKey,
    )

    if (!parameterDefinitionId) {
      return new Map<
        string,
        ValidationErrorWithLocation<DependencyValidationError>[]
      >()
    }

    return new Map(
      Array.from(
        parameterValueErrorsForCurrentParameterDefinition
          ?.get(parameterDefinitionId)
          ?.entries() ?? [],
      ).filter(([operationKey]) => valueOperationKeys.includes(operationKey)),
    )
  }, [
    parameterDefinitionId,
    parameterValueErrorsForCurrentParameterDefinition,
    resolvedParameterValueOperations,
  ])

  const errorsForDefinitionOperations = useMemo(() => {
    if (!parameterDefinitionId) {
      return []
    }

    const currentDefinitionOperationKey =
      parameterDefinitionOperation?.internalOperation.operationKey

    return currentDefinitionOperationKey
      ? (definitionErrorsForCurrentParameterDefinition
          ?.get(parameterDefinitionId)
          ?.get(currentDefinitionOperationKey) ?? [])
      : []
  }, [
    definitionErrorsForCurrentParameterDefinition,
    parameterDefinitionId,
    parameterDefinitionOperation?.internalOperation.operationKey,
  ])
  // endregion: Parameters visual model hooks

  const detailsPath = decodeURI(currentHash.substring(1))
  const viewingDetails = useMemo(
    () =>
      detailsPath && parameterDefinitionOperation?.internalOperation.entity
        ? getParameterDetailsFieldFromPath({
            entity: parameterDefinitionOperation?.internalOperation.entity,
            path: detailsPath,
          })
        : undefined,
    [detailsPath, parameterDefinitionOperation],
  )

  return (
    <Provider
      value={{
        parameterDefinitionOperation,
        parameterValueEntityOperationsForView,
        amendOperationsInView,
        allParameterValueOperations: resolvedParameterValueOperations,
        parameterDefinitionErrors: errorsForDefinitionOperations,
        parameterValueErrors: errorsForValueOperations,
        hasMoreValueOperations: hasMore,
        viewingDetails,
        detailsPath,
        onReviewOperation: setReviewingOperationKey,
        reviewingOperation,
      }}
    >
      {children}
    </Provider>
  )
}

export { ParameterProvider }
